import {
  FieldPath,
  RegisterOptions,
  UseFormGetValues,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form';

import { Result } from 'neverthrow';

import { DatePickerProps } from 'components/DatePicker';
import { QuoteCardType } from 'components/QuoteCard';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { QuickQuoteConfig } from 'contexts/serviceContext';
import { DATLaneHistoryEntry } from 'lib/api/getDATLaneHistory';
import { LTLShiplifyLookupMetadata } from 'lib/api/getLTLQuickQuotes';
import { LaneHistoryResponse } from 'lib/api/getLaneHistory';
import { LaneHistoryFromServiceResponse } from 'lib/api/getLaneHistoryFromService';
import {
  QuickQuoteResponse,
  QuickQuoteResponseQuote,
} from 'lib/api/getQuickQuote';
import { SubmitQuoteToTMSResponse } from 'lib/api/submitQuoteToTMS';
import { StopTypes } from 'types/Appointment';
import { Email } from 'types/Email';
import { IntegrationCore } from 'types/Integration';
import { TMSCustomer } from 'types/Load';
import { TransportType } from 'types/QuoteRequest';
import { Maybe, MaybeUndef } from 'types/UtilityTypes';
import { ApiError } from 'types/api/ApiError';
import { integrationNameMap } from 'types/enums/Integrations';
import { titleCase } from 'utils/formatStrings';
import { GenericCompanySearchableFields } from 'utils/loadInfoAndBuilding';

import { DATLaneHistoryChartEntry } from './LaneHistory/DATLaneHistory';

// Quick Quote mode. Drumkit calls truckload "TL" (never "FTL") in the UI and in code.
// The backend also accepts legacy "FTL" from older sidebar builds.
export enum QuoteMode {
  TL = 'TL',
  LTL = 'LTL',
}

// Parses a suggestion's mode (Drumkit LoadMode from the backend) into a Quick Quote mode.
// Empty or missing means truckload: most TL quote requests are stored without a mode, and the
// Quick Quote form already opens them as TL. Returns null for other modes (e.g. "Rail") so
// callers can decide how to handle them.
export function parseSuggestedQuoteMode(mode: unknown): QuoteMode | null {
  if (mode === undefined || mode === null) {
    return QuoteMode.TL;
  }
  if (typeof mode !== 'string') {
    return null;
  }

  // TODO(deprecate-FTL): accept "FTL" from old data and fixtures; remove once nothing sends it.
  switch (mode.trim().toUpperCase()) {
    case '':
    case QuoteMode.TL:
    case 'FTL':
      return QuoteMode.TL;
    case QuoteMode.LTL:
      return QuoteMode.LTL;
    default:
      return null;
  }
}

export enum RatingError {
  DIMENSIONS_REQUIRED = 'Dimensions Required for rating',
  INVALID_COMMODITY_LENGTH = 'Invalid Commodity Length',
  PICKUP_DATE_WEEKEND_HOLIDAY = 'Pickup Date cannot be on a weekend or holiday',
  DIMENSIONS_CANNOT_BE_ZERO = 'Dimensions cannot be 0',
  PROCESSED_TOTAL_RETURNED_ZERO = 'Processed total and quoted total from API both returned $0',
  NO_QUOTES_RETURNED = 'No quotes returned',
}

export interface TransportTypeOption {
  value: TransportType;
  label: string;
}

export type SubTransportTypeOption = {
  value: string;
  label: string;
  group: string;
  parentTransportType: TransportType;
};

export const getSubTransportTypeOptions = (
  subTransportTypes?: Maybe<SubTransportTypeOption[]>
): SubTransportTypeOption[] => {
  if (!subTransportTypes?.length) {
    return [];
  }

  return subTransportTypes.map((option) => ({
    value: option.value,
    label: option.label,
    group: option.group,
    parentTransportType: option.parentTransportType as TransportType,
  }));
};

export const getSubTransportTypeSelectOptions = (
  subTransportTypeOptions: SubTransportTypeOption[]
) =>
  subTransportTypeOptions.map((option) => ({
    value: option.value,
    label: option.label,
    group: option.group,
  }));

export const getParentTransportTypeForSubTransportType = (
  subTransportType: string,
  subTransportTypeOptions: SubTransportTypeOption[]
): Maybe<TransportType> => {
  const match = subTransportTypeOptions.find(
    (option) => option.value === subTransportType
  );
  return match?.parentTransportType ?? null;
};

export const getEquipmentDisplayLabel = (
  subTransportType: MaybeUndef<string>,
  transportType: Maybe<string>,
  subTransportTypeOptions: SubTransportTypeOption[],
  transportTypeOptions: TransportTypeOption[] = []
): string => {
  if (subTransportType) {
    const subMatch = subTransportTypeOptions.find(
      (option) => option.value === subTransportType
    );
    if (subMatch) {
      return subMatch.label;
    }
  }

  if (transportType) {
    const parentMatch = transportTypeOptions.find(
      (option) => option.value === transportType
    );
    if (parentMatch) {
      return parentMatch.label;
    }
  }

  return transportType ? titleCase(transportType) : '';
};

/**
 * Joins names into a readable list: ["DAT"] -> "DAT", ["DAT","Greenscreens"] ->
 * "DAT and Greenscreens", ["A","B","C"] -> "A, B, and C".
 */
const formatIntegrationNameList = (names: string[]): string => {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
};

export const getGranularEquipmentTooltip = (
  quotingIntegrations: IntegrationCore[] = [],
  tmsIntegrations: IntegrationCore[] = []
): string => {
  const quotingNames = Array.from(
    new Set(
      quotingIntegrations.map(
        (integration) =>
          integrationNameMap[
            integration.name as keyof typeof integrationNameMap
          ] ?? integration.name
      )
    )
  );
  // Omit "like ..." when we have no named engines so we don't get "like your quoting engines".
  const quotingIntro =
    quotingNames.length > 0
      ? `Quoting engines like ${formatIntegrationNameList(quotingNames)}`
      : 'Quoting engines';
  // Named TMS: "the Aljex ..."; fallback: "your TMS ..." (no leading "the").
  const equipmentPhrase =
    tmsIntegrations.length > 0
      ? `the ${
          integrationNameMap[
            tmsIntegrations[0].name as keyof typeof integrationNameMap
          ] ?? tmsIntegrations[0].name
        } granular equipment type`
      : 'your TMS granular equipment type';

  return `${quotingIntro} support only generic transport types. Pick ${equipmentPhrase} and Drumkit will automatically map it to the matching value in your quoting engines.`;
};

export interface LTLCustomer {
  customerId: string;
  customerName: string;
  externalId?: string;
  customerDbId?: number;
  /** 3G-only: when true, compute implied freight class from dimensions/weight and filter commodities. */
  enableAutoFreightClass?: boolean;
}

export const AUTO_TRANSPORT_OPTION: TransportTypeOption = {
  value: TransportType.AUTO,
  label: 'Auto Transport',
};

type TransportTypeOptionsSettings = {
  // Auto transport is only quotable through Super Dispatch.
  includeAuto?: boolean;
};

export const getTransportTypeOptions = (
  quickQuoteConfig?: Maybe<QuickQuoteConfig>,
  { includeAuto = false }: TransportTypeOptionsSettings = {}
): TransportTypeOption[] => {
  const options: TransportTypeOption[] = [
    {
      value: TransportType.VAN,
      label: 'Van',
    },
    {
      value: TransportType.REEFER,
      label: 'Reefer',
    },
    {
      value: TransportType.FLATBED,
      label: 'Flatbed',
    },
    {
      value: TransportType.HOTSHOT,
      label: 'Hotshot',
    },
    {
      value: TransportType.BOXTRUCK,
      label: 'Box Truck (Van)',
    },
  ];
  if (includeAuto) {
    options.unshift(AUTO_TRANSPORT_OPTION);
  }
  if (
    quickQuoteConfig?.otherTransportTypes?.some(
      (t) => t.toUpperCase() === TransportType.SPRINTER
    )
  ) {
    options.push({
      value: TransportType.SPRINTER,
      label: 'Sprinter (Van)',
    });
  }
  if (
    quickQuoteConfig?.otherTransportTypes?.some(
      (t) => t.toUpperCase() === TransportType.DRAYAGE
    )
  ) {
    options.push({
      value: TransportType.DRAYAGE,
      label: 'Drayage (Van)',
    });
  }
  return options;
};

export enum QuoteCountries {
  USA = 'US',
  Canada = 'CA',
}

// TODO: Deduplicate with QuoteTypeInSource and remove
export enum SelectedQuoteType {
  GS_NETWORK = 'GS_NETWORK',
  GS_BUYPOWER = 'GS_BUYPOWER',
  DAT_RATEVIEW = 'DAT_RATEVIEW',
  DAT_LONGEST_LEG = 'DAT_LONGEST_LEG', // For multi-stop quotes
  DAT_LEG_TO_LEG = 'DAT_LEG_TO_LEG', // For multi-stop quotes
  TRUCKSTOP_POSTED = 'TRUCKSTOP_POSTED',
  TRUCKSTOP_BOOKED = 'TRUCKSTOP_BOOKED',
  KENCO_FLASH_AVERAGE = 'KENCO_FLASH_AVERAGE',
  SONAR = 'SONAR',
  TRANSFIX = 'TRANSFIX',
  SUPER_DISPATCH = 'SUPER_DISPATCH',
  TRUPAY = 'TRUPAY',
  LTL_RATE = 'LTL_RATE',

  // Lane history types
  MCLEOD_LANE_HISTORY = 'MCLEOD_LANE_HISTORY',
  TURVO_LANE_HISTORY = 'TURVO_LANE_HISTORY',
  GLOBALTRANZ_LANE_HISTORY = 'GLOBALTRANZ_LANE_HISTORY',
  GLOBALTRANZ_TMS_LANE_HISTORY = 'GLOBALTRANZ_TMS_LANE_HISTORY',
  OLYMPUS_DAT_LANE_HISTORY = 'OLYMPUS_DAT_LANE_HISTORY',
  ALJEX_LANE_HISTORY = 'ALJEX_LANE_HISTORY',
  LANE_HISTORY = 'LANE_HISTORY',
}

export enum QuoteTypeInSource {
  GS_Network = 'gs_network',
  GS_BuyPower = 'gs_buypower',
  DAT = 'dat_spot',
  DAT_LONGEST_LEG = 'dat_multi_stop_longest_leg', // For multi-stop quotes
  DAT_LEG_TO_LEG = 'dat_multi_stop_leg_to_leg', // For multi-stop quotes
  GlobalTranz = 'globaltranz',
  TruckStop_Booked = 'truckstop_booked',
  TruckStop_Posted = 'truckstop_posted',
  KencoAverage = 'kenco_average',
  SonarTrac = 'sonar_trac',
  TransfixSpot = 'transfix_spot',
  SuperDispatchRecommended = 'superdispatch_recommended',
  TruPayAllIn = 'trupay_all_in',
  TMSLaneHistory = 'tmslanehistory',
}

export enum AutoTrailerType {
  Open = 'open',
  Enclosed = 'enclosed',
}

export type AutoTransportVehicle = {
  year: string;
  make: string;
  model: string;
  // Drumkit enum, SD alias (e.g. 2_door_coupe), or empty until inferred/selected.
  type: string;
  isInoperable: boolean;
};

export type AutoTransportDetails = {
  trailerType: AutoTrailerType;
  vehicles: AutoTransportVehicle[];
};

export enum ProfitType {
  // EX: If CarrierCostType = PerMile & ProfitType = Amount, then profit is in $ per mile
  // EX: If CarrierCostType = Flat & ProfitType = Amount, then profit is total $ value
  Amount = 'Amount',
  Percentage = 'Percentage',
}

export enum CarrierCostType {
  Flat = 'Flat',
  PerMile = 'PerMile',
  Linehaul = 'Linehaul',
}

export enum DATQuoteTimeframe {
  '3_DAYS' = '3 day',
  '7_DAYS' = '7 day',
  '15_DAYS' = '15 day',
  '30_DAYS' = '30 day',
  '60_DAYS' = '60 day',
  '90_DAYS' = '90 day',
  '180_DAYS' = '180 day',
  '365_DAYS' = '365 day',
  '1_MONTH' = '1 month',
}

export enum DATQuoteLocationType {
  '3_DIGIT_ZIP' = '3_DIGIT_ZIP',
  MARKET_AREA = 'MARKET_AREA',
  EXTENDED_MARKET_AREA = 'EXTENDED_MARKET_AREA',
  STATE = 'STATE',
  REGION = 'REGION',
  COUNTRY = 'COUNTRY',
}

// Generic Drumkit enums
export enum LaneTier {
  _3DigitZipLaneTier = '3-Digit-Zip',
  CityStateLaneTier = 'City-to-City',
  MarketLaneTier = 'Market-to-Market', // Not supported by Mcleod rn
  StateLaneTier = 'State-to-State',
}

export const laneTierMap: Record<LaneTier, string> = {
  [LaneTier._3DigitZipLaneTier]: '3-digit zip',
  [LaneTier.CityStateLaneTier]: 'City',
  [LaneTier.MarketLaneTier]: 'Market',
  [LaneTier.StateLaneTier]: 'State',
};

export interface QuoteFormStop {
  order: number;
  // Frontend-only for now; will be used to rank location suggestions in ENG-8377
  placeId?: number;
  city: string;
  state: string;
  zip: string;
  country?: QuoteCountries;
  // Frontend-only field for form input (not sent to backend)
  location?: string;
  // Frontend-only display label for selected location option.
  locationDisplay?: string;
  // Optional location metadata from geo-search response.
  externalTMSID?: string;
  timezone?: string;
  addressName?: string;
  addressLine1?: string;
  addressLine2?: string;
}

export const EMPTY_STOP: QuoteFormStop = {
  order: 0,
  zip: '',
  city: '',
  state: '',
  location: '',
};

export interface LTLBuySellBreakdown {
  buyRate: number;
  sellRate: number;
  fuelRate: {
    buy: number;
    sell: number;
  };
  total: {
    buy: number;
    sell: number;
  };
  margin: {
    buyRate: number; // percentage
    fuelRate: number; // percentage
    total: number; // percentage
  };
}

interface LTLTaiMargin {
  markupAmount: number;
}

interface LTLTaiAccessorialPrice {
  buy: number;
  sell: number;
  accessorialName: string;
}

interface LTLTurvoRateMetadata {
  ltlProviderQuoteId?: string;
  pricingType?: string;
  payable?: number;
  billable?: number;
  margin?: number;
  estimatedDelivery?: string;
  serviceType?: string;
  transitDays?: number;
  paymentTerms?: string;
  serviceArea?: string;
  pickupServices?: unknown;
  deliveryServices?: unknown;
}

export interface LTLQuoteLaneAddress {
  city?: string;
  state?: string;
  zip?: string;
}

export interface LTLQuote {
  id: number;
  externalID?: string;
  pickupLocation?: LTLQuoteLaneAddress;
  deliveryLocation?: LTLQuoteLaneAddress;
  carrierName: string;
  carrierSCAC: string;
  tariffName?: string;
  totalCost: number;
  transitDays: number;
  serviceLevel: string;
  guaranteed: boolean;
  isVolume: boolean;
  expiresAt?: string;
  coverageAmountNew?: MaybeUndef<number>;
  coverageAmountUsed?: MaybeUndef<number>;
  coverageType?: string;
  coverageNote?: string;
  isError: boolean;
  notServiced: boolean;
  errorMessage?: string;
  distance?: number;
  targetBuyRate?: number;
  minMarkup?: number;
  maxMarkup?: number;
  rateMetadata?: {
    // Buy rates
    buyAccessorialTotalAmount?: number;
    buyFuelPercentage?: number;
    buyFuelSurcharge?: number;
    buyRateFreightCharges?: number;
    buyRateTotal?: number;

    // Sell rates
    sellAccessorialTotalAmount?: number;
    sellFuelPercentage?: number;
    sellFuelSurcharge?: number;
    sellRateFreightCharges?: number;
    sellRateTotal?: number;

    // Accessorial prices and margins
    accessorialPrices?: Maybe<LTLTaiAccessorialPrice[]>;
    margins?: LTLTaiMargin[];

    // ThreeG-specific fields (optional)
    isThreeG?: boolean;
    threeGNetCost?: number;
    threeGNetFreight?: number;
    threeGNetAccessorials?: number;
    threeGProfitMarkup?: number;
    threeGMarginPercentage?: string;

    // Turvo-specific fields (optional)
    isTurvo?: boolean;
    ltlProviderQuoteId?: LTLTurvoRateMetadata['ltlProviderQuoteId'];
    pricingType?: LTLTurvoRateMetadata['pricingType'];
    payable?: LTLTurvoRateMetadata['payable'];
    billable?: LTLTurvoRateMetadata['billable'];
    margin?: LTLTurvoRateMetadata['margin'];
    estimatedDelivery?: LTLTurvoRateMetadata['estimatedDelivery'];
    serviceType?: LTLTurvoRateMetadata['serviceType'];
    transitDays?: LTLTurvoRateMetadata['transitDays'];
    paymentTerms?: LTLTurvoRateMetadata['paymentTerms'];
    serviceArea?: LTLTurvoRateMetadata['serviceArea'];
    pickupServices?: LTLTurvoRateMetadata['pickupServices'];
    deliveryServices?: LTLTurvoRateMetadata['deliveryServices'];
  };
}

export interface LTLQuoteResultsProps {
  quotes: LTLQuote[];
  selectedQuote: Maybe<LTLQuote>;
  onSelectQuote: (quote: LTLQuote) => void;
  isLoading?: boolean;
}

export const LTL_FREIGHT_CLASSES = [
  '50',
  '55',
  '60',
  '65',
  '70',
  '77.5',
  '85',
  '92.5',
  '100',
  '125',
  '175',
  '250',
  '300',
  '400',
] as const;

export const LTL_DIMENSION_UNIT_LABEL_MAP: Record<string, string> = {
  Inch: 'in',
  Foot: 'ft',
  Yard: 'yd',
  Centimeter: 'cm',
  Meter: 'm',
  Millimeter: 'mm',
};

export const LTL_WEIGHT_UNIT_LABEL_MAP: Record<string, string> = {
  Pound: 'lb',
  Kilogram: 'kg',
  Tonne: 't',
  Ton: 'ton',
  Ounce: 'oz',
  Gram: 'g',
  'Long Ton': 'long ton',
};

export const LTL_WEIGHT_UNIT_OPTIONS = [
  'Pound',
  'Kilogram',
  'Tonne',
  'Ounce',
  'Ton',
  'Gram',
  'Long Ton',
] as const;

export interface LTLItem {
  itemId: string;
  description: string;
  length?: number;
  width?: number;
  height?: number;
  weight?: number;
  // Optional per-item units (currently used by Turvo LTL UI only)
  dimensionUnit?: string;
  weightUnit?: string;
  handlingUnits?: number;
  pieces?: number;
  packageType?: number;
  // Per-item freight class (when selected from commodity dropdown, e.g. ThreeG)
  freightClass?: string;
  // ThreeG-only: when true, density changes do not overwrite freightClass.
  freightClassLocked?: boolean;
  // Calculated freight class based on dimensions/weight (Tai)
  estimatedFreightClass?: string;
  // True when user explicitly opts into the estimated class (Tai)
  usingEstimatedFreightClass?: boolean;
  // Whether the estimated class warning is currently shown (Tai)
  showEstimatedFreightClassMessage?: boolean;
  // Per-item NMFC (when selected from commodity dropdown or entered manually)
  nmfcCode?: string;
  // Selected commodity external TMS ID (ThreeG lineItems.commodityExternalTmsId)
  commodityExternalTmsId?: string;
  // Handling unit type (e.g. "1" Letter, "2" YourPackaging, "3" Pallet)
  handlingUnitType?: string;
  // Tai-only: allow side-loading optimization (swap length/width) for this item.
  canSideLoad?: boolean;
  // When applying partial HU sideloading, derived items link back to the original.
  parentItemId?: string;
  // Side-load split depth guard: rows at/above max split depth are never split.
  splitDepth?: number;
  linearLength?: number;
  minTemp?: number;
  maxTemp?: number;
  nonStackable?: boolean;
  hazmat?: boolean;
  unClass?: string;
  hazmatContactNumber?: string;
  packingGroup?: string;
  hazmatClass?: string;
  properShippingName?: string;
  specialProvisions?: string;
  subsidiaryClass?: string;
  hazmatContactName?: string;
  erapReference?: string;
}

// ThreeG handling unit type options (single source for dropdown and payload).
export const HANDLING_UNIT_TYPES_THREEG = [
  { label: 'Letter', value: '1' },
  { label: 'Pallet', value: '3' },
  { label: 'YourPackaging', value: '2' },
];

export const LTL_UNIT_SYSTEMS = {
  IMPERIAL: 'in/lbs',
  METRIC: 'cm/kg',
} as const;

export type LTLUnitSystem =
  (typeof LTL_UNIT_SYSTEMS)[keyof typeof LTL_UNIT_SYSTEMS];

export const LTL_THRESHOLDS = {
  MAX_CUBIC_FEET: 700,
  MAX_WEIGHT_LBS: 19999,
  MAX_LINEAR_FEET: 12,
  TRAILER_WIDTH_INCHES: 96,
} as const;

// Tai side-load search is O(items x passes x candidates); skip it for small
// shipments. Run when either:
// - linear feet is within 1 ft of LTL_THRESHOLDS.MAX_LINEAR_FEET, or
// - total cubic feet is within 50 ft^3 of LTL_THRESHOLDS.MAX_CUBIC_FEET
//   (close to the 700 ft^3 limit).
export function shouldRunTaiSideLoadSearch(metrics: {
  linearFeet: number;
  totalCubicFeet: number;
}): boolean {
  return (
    metrics.linearFeet >= LTL_THRESHOLDS.MAX_LINEAR_FEET - 1 ||
    metrics.totalCubicFeet >= LTL_THRESHOLDS.MAX_CUBIC_FEET - 50
  );
}

export interface LTLCommodityInfo {
  freightClass?: string;
  nmfcCode?: string;
  totalWeight?: number;
  stackable?: boolean;
  sideLoadEnabled?: boolean;
  allowSplitMerge?: boolean;
  hazmat?: boolean;
  units?: LTLUnitSystem;
  linearFeet?: number;
  autoCalculateLinearFeet?: boolean;
}

export interface LTLValueInfo {
  declaredValue?: number;
  deductibleValue?: number;
  shipValue?: number;
}
export interface ThreeGShipmentTotals {
  linesOverride?: boolean;
  wtGrsTot?: number;
  wtNetTot?: number;
  volGrsTot?: number;
  volNetTot?: number;
  pieceTot?: number;
  handlingUnits?: number;
  linearLengthTot?: number;
  billableWeight?: number;
}
export interface ThreeGRateInfo {
  doNotOverwrite?: boolean;
  needsReview?: 'Needs Review' | 'Reviewed';
}

export const NEEDS_REVIEW_OPTIONS_THREEG = [
  { value: 'Needs Review', label: 'Needs Review' },
  { value: 'Reviewed', label: 'Reviewed' },
] as const;

export const RATE_TYPE_OPTIONS_THREEG = [
  'Area',
  'Density',
  'Dist',
  'Duration',
  'FlatRate',
  'Index',
  'LinearLength',
  'PercentMarkup',
  'PerPalletHandlingUnit',
  'Pieces',
  'Tariff',
  'TrailerLengthUsage',
  'Units',
  'Vol',
  'WebService',
  'Wt',
] as const;

export type ThreeGRateType = (typeof RATE_TYPE_OPTIONS_THREEG)[number];

export interface ThreeGQuoteCharge {
  chargeId: string;
  chargeType?: 'Accessorial' | 'Freight';
  qty?: number;
  netAmt?: number;
  benchmark?: number;
  netCost?: number;
  gainshare?: number;
  grossAmt?: number;
  discAmt?: number;
  thirdPartyChg?: number;
  rateType?: string;
  details?: string;
  accCode?: string;
}
export interface ThreeGRatedServiceInfo {
  ratedParcelService?: string;
  ratedLtlService?: string;
  ratedIntermodalService?: string;
  estDelivery?: Date;
  estTransitDays?: number;
  directIndirect?: string;
  quoteRefNum?: string;
  effectiveDate?: Date;
  expirationDate?: Date;
}
export interface ThreeGCommentItem {
  id: string;
  commentType?: string;
  commentTypeLabel?: string;
  stopNumeric?: number;
  comment?: string;
}

export type ThreeGProfileType = 'Equipment' | 'Carrier' | 'Mode';

export interface ThreeGCompatibilityProfileItem {
  id: string;
  profileType?: ThreeGProfileType;
  profileName?: string;
  profileNameLabel?: string;
  /** Set when profiles are loaded from the trading partner; sidebar treats them as view-only. */
  isReadOnly?: boolean;
}

export interface ThreeGOtherConstraints {
  equipment?: string;
  carrierTradingPartnerId?: string;
  carrierLabel?: string;
  mode?: string;
  serviceOption?: string;
  parcelService?: string;
  ltlService?: string;
  intermodalService?: string;
}

export const RATED_PARCEL_SERVICE_OPTIONS = [
  { value: 'Ground', label: 'Ground' },
  { value: 'GroundHomeDelivery', label: 'Ground - Home Delivery' },
  { value: 'InternationalExpedited', label: 'International - Expedited' },
  { value: 'InternationalFirst', label: 'International - First' },
  {
    value: 'InternationalOneToThree',
    label: 'International - One To Three Days',
  },
  { value: 'InternationalStd', label: 'International - Standard' },
  { value: 'NextDayFirst', label: 'Next Day - First' },
  { value: 'NextDayMidMorning', label: 'Next Day - Mid Morning' },
  { value: 'NextDayStd', label: 'Next Day - Standard' },
  { value: 'ThreeDay', label: 'Three Day' },
  { value: 'TwoDayFirst', label: 'Two Day - First' },
  { value: 'TwoDayStd', label: 'Two Day - Standard' },
] as const;

export const RATED_LTL_SERVICE_OPTIONS = [
  { value: 'Expedited', label: 'Expedited' },
  { value: 'Guaranteed', label: 'Guaranteed' },
  { value: 'Standard', label: 'Standard' },
] as const;

export const RATED_INTERMODAL_SERVICE_OPTIONS = [
  { value: 'Expedited', label: 'Expedited' },
  { value: 'Standard', label: 'Standard' },
] as const;

export const DIRECT_INDIRECT_OPTIONS = [
  { value: 'Direct', label: 'Direct' },
  { value: 'Indirect', label: 'Indirect' },
] as const;

/** ThreeG LTL packing group options (static dropdown). */
export const PACKING_GROUP_OPTIONS_THREEG = [
  { value: '4', label: 'PG I' },
  { value: '3', label: 'PG II' },
  { value: '2', label: 'PG III' },
] as const;

/** ThreeG LTL hazmat classification options (static dropdown). */
export const HAZMAT_CLASS_OPTIONS_THREEG = [
  {
    value: '5',
    label: 'Class 3 (Flammable Liquids) 100 degrees F or less open cup',
  },
  {
    value: '252',
    label:
      'Class 4.1 (Hazard class 4.1: Flammable solids, self-reactive substances, polymerizing substances and solid)',
  },
  { value: '226', label: 'Class 4.3 (dangerous when wet)' },
  { value: '6', label: 'Class 5.1 Oxidizers' },
  { value: '7', label: 'Class 5.2 Organic Peroxides' },
  { value: '9', label: 'Class 6.1 (Toxic Substances)' },
  { value: '8', label: 'Class 8 (Corrosives)' },
  { value: '1', label: 'Class 9 (Misc. dangerous goods)' },
] as const;

export enum KencoUrgency {
  CanGet = 'can',
  MustGet = 'must',
}

export const KencoUrgencyLabel = {
  [KencoUrgency.CanGet]: 'Can Get',
  [KencoUrgency.MustGet]: 'Must Get',
};

export interface QuickQuoteInputs {
  loadId?: string;
  transportType: TransportType;
  subTransportType?: string;
  pickupDate: Date;
  // Tai LTL: pickup close/end time. Other flows may leave this null.
  pickupCloseDate: Maybe<Date>;
  deliveryDate: Date;
  // Tai LTL: delivery close/end time. Optional; other flows may leave null.
  deliveryCloseDate: Maybe<Date>;
  stops: Array<QuoteFormStop>;
  customerName: string;
  quoteContactName?: string;
  ltl?: {
    commodity?: LTLCommodityInfo;
    items?: LTLItem[];
    selectedAccessorials: LTLSelectedAccessorial[];
    value?: LTLValueInfo;
    // Turvo LTL service type (e.g. "any", "overnight-am"). Sent as transportationType to BE.
    serviceType?: string;
    // Turvo LTL `paymentTerm`(e.g. "prepaid", "collect", "third-party").
    paymentTerm?: string;
    // Turvo LTL pickup services selected from Turvo's allowed catalog.
    pickupServices?: string[];
    // Turvo LTL delivery services selected from Turvo's allowed catalog.
    deliveryServices?: string[];
    shipmentTotals?: ThreeGShipmentTotals;
    rateInfo?: ThreeGRateInfo;
    quoteCharges?: ThreeGQuoteCharge[];
    ratedServiceInfo?: ThreeGRatedServiceInfo;
    comments?: ThreeGCommentItem[];
    otherConstraints?: ThreeGOtherConstraints;
    compatibilityProfiles?: ThreeGCompatibilityProfileItem[];
    // Current per-stop lookup state sent with the next rate request.
    shiplifyLookups?: Partial<Record<StopTypes, LTLShiplifyLookupMetadata>>;
    // Shiplify recommendations explicitly dismissed by the user.
    dismissedShiplifyRecommendations?: string[];
  };
  isSubmitToTMS?: boolean;
  quoteNumber?: string;
  quoteExpirationDate?: Date;
  quoteEta?: Date;

  // Integration-specific fields
  kencoUrgency?: KencoUrgency;
  autoTransport?: AutoTransportDetails;

  // Custom (Non-TMS) accessorials stored in DB
  customAccessorials?: string[];
}

export type QuickQuoteTextInputProps = React.ComponentPropsWithoutRef<
  typeof RHFTextInput
> & {
  name: FieldPath<QuickQuoteInputs> | FieldPath<BatchQuoteFormValues>;
};

export type QuickQuoteDatePickerProps = {
  name: FieldPath<QuickQuoteInputs>;
  rules?: RegisterOptions<QuickQuoteInputs, FieldPath<QuickQuoteInputs>>;
} & Omit<DatePickerProps, 'field' | 'highlightError'>;

export type SendGreenscreensQuoteProps = {
  quote: QuickQuoteResponse;
  setGreenscreensQuoteID: React.Dispatch<React.SetStateAction<Maybe<string>>>;
};

export type SendUserQuoteProps = {
  email: Maybe<Email>;
  quoteResponse: Maybe<QuickQuoteResponse>;
  parentQuoteRequestId: number;
  greenscreensQuoteID: Maybe<string>;
  carrierCost: number;
  profit: number;
  profitType: ProfitType;
  finalPrice: number;
  draftResponse: string;
};

export type FetchQuoteNumberProps = {
  email: Maybe<Email>;
  setHasThirdPartyQuoteURLs: React.Dispatch<React.SetStateAction<boolean>>;
  setValue: UseFormSetValue<QuickQuoteInputs>;
};

export type DATQuoteMetadata = {
  timeframe: keyof typeof DATQuoteTimeframe;
  companies: number;
  reports: number;
  originName: string;
  originType: keyof typeof DATQuoteLocationType;
  destinationName: string;
  destinationType: keyof typeof DATQuoteLocationType;
};

export type FetchCustomersProps = {
  setInitialCustomers: React.Dispatch<
    React.SetStateAction<Maybe<TMSCustomer[]>>
  >;
  setCustomers: React.Dispatch<React.SetStateAction<Maybe<TMSCustomer[]>>>;
  setTMSTenant: React.Dispatch<React.SetStateAction<MaybeUndef<string>>>;
  tmsIntegrations: IntegrationCore[];
};

export type HandleRefreshCustomersProps = {
  setInitialCustomers: React.Dispatch<
    React.SetStateAction<Maybe<TMSCustomer[]>>
  >;
  setCustomers: React.Dispatch<React.SetStateAction<Maybe<TMSCustomer[]>>>;
  tmsIntegrations: IntegrationCore[];
};

export type HandleResetCustomerSearchProps = {
  setCustomers: React.Dispatch<React.SetStateAction<Maybe<TMSCustomer[]>>>;
  initialCustomers: Maybe<TMSCustomer[]>;
};

export type HandleCustomerSearchProps = {
  field: GenericCompanySearchableFields;
  value: string;
  tmsIntegrations: IntegrationCore[];
  customers: Maybe<TMSCustomer[]>;
  setCustomers: React.Dispatch<React.SetStateAction<Maybe<TMSCustomer[]>>>;
};

export type EnableDATIndividualAccessProps = {
  datEmailAddress: string;
  setHasGrantedDATPermissions: React.Dispatch<React.SetStateAction<boolean>>;
};

export type ProcessQuoteTMSSubmissionProps = {
  customerId: string;
  finalPrice: Maybe<number>;
  getValues: UseFormGetValues<QuickQuoteInputs>;
  setError: UseFormSetError<QuickQuoteInputs>;
};

export type ParseQuoteCardsFromResponseProps = {
  newQuote: QuickQuoteResponse;
  formValues: QuickQuoteInputs;
  setDATFuelSurcharge: (datFuelSurcharge: number) => void;
};

export type BatchQuoteResult = {
  response: Maybe<QuickQuoteResponse>;
  cards: QuoteCardType[];
};

export interface HelperFunctions {
  toTitleCase: (str: string) => string;
  parseLocation: (location: string) => Maybe<{
    zip: string;
    city: string;
    state: string;
    country: QuoteCountries;
  }>;
  sendGreenscreensQuote: (props: SendGreenscreensQuoteProps) => Promise<void>;
  sendUserQuote: (props: SendUserQuoteProps) => Promise<void>;
  fetchQuoteNumber: (props: FetchQuoteNumberProps) => Promise<void>;
  fetchLaneHistoryFromService: (props: {
    quoteRequestId: number;
    originCity: string;
    originState: string;
    originZip: string;
    originCountry: string;
    destinationCity: string;
    destinationState: string;
    destinationZip: string;
    destinationCountry: string;
    transportType: string;
  }) => Promise<Result<LaneHistoryFromServiceResponse, ApiError>>;
  fetchCustomers: (props: FetchCustomersProps) => Promise<void>;
  enableDATIndividualAccess: (
    props: EnableDATIndividualAccessProps
  ) => Promise<void>;
  handleQuoteLaneHistory: (
    laneHistoryResponse: LaneHistoryResponse,
    // TODO: Implement multi-stop lane history ENG-3759.
    // Until then, we show a warning to the user that the card is derived from 2-stop data,
    // from the first and last stop in the form (see LaneHistoryTooltipConstructor)
    originalStops: QuoteFormStop[]
  ) => QuoteCardType[];
  processQuoteTMSSubmission: (
    props: ProcessQuoteTMSSubmissionProps
  ) => Promise<MaybeUndef<SubmitQuoteToTMSResponse>>;
  parseQuoteCardsFromResponse: (
    props: ParseQuoteCardsFromResponseProps
  ) => QuoteCardType[];
  validateLTLForm: (
    formValues: QuickQuoteInputs,
    setError: UseFormSetError<QuickQuoteInputs>
  ) => Array<[FieldPath<QuickQuoteInputs>, string]>;
  getQuoteCards: (props: {
    quotes: QuickQuoteResponseQuote[];
    updatedFormValues: QuickQuoteInputs;
    isMultiStop: boolean;
  }) => QuoteCardType[];
  formatDATLaneHistoryRates: (
    rates: DATLaneHistoryEntry[]
  ) => DATLaneHistoryChartEntry[];
}

export interface BatchQuoteFormInput extends QuickQuoteInputs {
  profit: number;
  profitType: ProfitType;
  /** Kit / curSuggestionList id for this lane - stable across form reindexes after remove. */
  suggestionId?: number;
}

export type BatchQuoteFormValues = {
  customerName: string;
  quotes: BatchQuoteFormInput[];
};

export type BatchEditValues = {
  transportType: string;
  subTransportType: string;
  pickupLocation: string;
  dropoffLocation: string;
  margin: string;
  marginType: ProfitType;
  buyRateSource: string;
  priceFormat: string;
};

// Batch quote email template
// TODO: use email_templates from backend, need to make batch quote email templates
export const BATCH_QUOTE_TEMPLATE = `{{#hasCustomer}}Hi {{customerName}},

{{/hasCustomer}}{{^hasCustomer}}Hi,

{{/hasCustomer}}Here are the requested quotes:

{{#quotes}}
Quote {{quoteNumber}}: {{route}}
Transport Type: {{transportType}}
Price: {{price}}

{{/quotes}}`;

export type LTLAccessorialCategoryTag = 'pickup' | 'delivery' | 'shipment';
export type LTLAccessorialFormType = 'text' | 'date';

export interface LTLAccessorial {
  code: string;
  name: string;
  externalId: number;
  categoryTags: LTLAccessorialCategoryTag[];
  /** Present when the accessorials API is called with customer context (e.g. Mohawk 3G). */
  isFavorite?: boolean;
  actions?: Record<string, LTLAccessorialFormType>;
}

export interface LTLSelectedAccessorial {
  code: string;
  name: string;
  externalId: number;
  actionValues?: Record<string, string>;
  recommendedByShiplify?: boolean;
  recommendedByShiplifyStop?: StopTypes;
  // Set when merged from a Tai Address book favorite (pickup or dropoff).
  fromAddressBookStop?: StopTypes;
  shiplifyRecommendationCode?: string;
  shiplifyConfidence?: string;
  shiplifyAccuracy?: number;
}

// Mapping from package type names to numeric codes
export const PACKAGE_TYPE_TO_CODE: Record<string, number> = {
  'A-Frame': 298,
  Bags: 290,
  Bales: 303,
  Barrel: 20,
  Basket: 30,
  Batteries: 310,
  Bins: 309,
  Box: 40,
  'Break Bulk': 170,
  Bundle: 45,
  Cans: 304,
  Cart: 297,
  Carton: 50,
  Case: 60,
  Chest: 70,
  Container: 190,
  Crate: 80,
  Cylinder: 90,
  Drum: 100,
  Gallons: 302,
  Gaylords: 294,
  HalfPallets: 296,
  Hopper: 299,
  Jerricans: 305,
  'Lift Van': 281,
  Loose: 307,
  Package: 110,
  Pails: 291,
  Pallet: 120,
  Piece: 130,
  Racks: 306,
  Reel: 140,
  Roll: 260,
  Skid: 150,
  Stacks: 308,
  Totes: 292,
  Truckload: 285,
  Tube: 160,
  Tubs: 301,
  'U-Box': 295,
  Unit: 280,
  Vehicle: 300,
  Wheels: 293,
};

// Reverse mapping from numeric codes to package type names
export const PACKAGE_CODE_TO_TYPE: Record<number, string> = Object.fromEntries(
  Object.entries(PACKAGE_TYPE_TO_CODE).map(([name, code]) => [code, name])
) as Record<number, string>;

export const DEFAULT_DIMENSION_UNIT = 'Inch';
export const DEFAULT_WEIGHT_UNIT = 'Pound';
